import { invoke, tauriOnlyInvoke } from "../invoke";
import { COMMIT_SAVE } from "../commitMessages";
import { getProjectBackend, isCloudRoot, isDemoRoot } from "../platform";
import {
  isTauri,
  normalizePayload,
  type EditorPayload,
  type RawEditorPayload,
} from "./core";

export type CloudBackupStatus = {
  headOid: string | null;
  lastUploadedOid: string | null;
  gitDirty: boolean;
};

function skipGitForVirtualRoot(rootPath: string): boolean {
  return isDemoRoot(rootPath) || (isTauri() === false && isCloudRoot(rootPath));
}

export async function cloudBackupStatus(
  rootPath: string,
): Promise<CloudBackupStatus> {
  if (skipGitForVirtualRoot(rootPath)) {
    return { headOid: null, lastUploadedOid: null, gitDirty: false };
  }
  const { git } = await getProjectBackend(rootPath);
  return git.cloudBackupStatus();
}

export async function flushProjectCommit(
  rootPath: string,
  message = COMMIT_SAVE,
): Promise<void> {
  if (skipGitForVirtualRoot(rootPath)) {
    return;
  }
  const backend = await getProjectBackend(rootPath);
  if (backend.kind === "disk") {
    // Desktop's coalesced-autosave scheduler — no `zw-online` equivalent,
    // saves there already commit synchronously per request.
    await tauriOnlyInvoke("flush_project_commit", { rootPath, message });
    return;
  }
  await backend.git.commitIfDirty(message);
}

/** Commit now without the cloud-pull handshake. Returns new tip oid if any. */
export async function commitProjectNow(
  rootPath: string,
  message = COMMIT_SAVE,
): Promise<string | null> {
  if (skipGitForVirtualRoot(rootPath)) {
    return null;
  }
  const { git } = await getProjectBackend(rootPath);
  return git.commitIfDirty(message);
}

/** Manuscript word total as of the newest commit at or before `beforeUnix` (seconds). */
export async function manuscriptWordsAtOrBefore(
  rootPath: string,
  beforeUnix: number,
  files: string[],
): Promise<number> {
  return invoke<number>("manuscript_words_at_or_before", {
    rootPath,
    beforeUnix,
    files,
  });
}

export type DeletedFileInfo = {
  relativePath: string;
  oid: string;
  message: string;
  /** Unix seconds. */
  time: number;
};

export async function listDeletedFiles(
  rootPath: string,
  limit = 100,
): Promise<DeletedFileInfo[]> {
  return invoke<DeletedFileInfo[]>("list_deleted_files", {
    rootPath,
    limit,
  });
}

/** Last live contents of a deleted path (parent of the delete commit). */
export async function previewDeletedFile(
  rootPath: string,
  relativePath: string,
  oid: string,
): Promise<string> {
  return invoke<string>("preview_deleted_file", {
    rootPath,
    relativePath,
    oid,
  });
}

export async function restoreDeletedFile(
  rootPath: string,
  relativePath: string,
  oid: string,
): Promise<EditorPayload> {
  const raw = await invoke<RawEditorPayload>("restore_deleted_file", {
    rootPath,
    relativePath,
    oid,
  });
  return normalizePayload(raw);
}
